// ==================================
// Pagina Configuracion de Plataforma
// ==================================

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Header } from '@/componentes/layout';
import { Button, Input, Card, CardHeader, Spinner } from '@/componentes/ui';
import { configuracionServicio } from '@/servicios';

export function Configuracion() {
  const queryClient = useQueryClient();

  const { data: config, isLoading } = useQuery({
    queryKey: ['configuracion'],
    queryFn: () => configuracionServicio.obtener(),
  });

  const [precioKit, setPrecioKit] = useState('');
  const [mesesGratisConKit, setMesesGratisConKit] = useState('');

  useEffect(() => {
    if (config) {
      setPrecioKit(config.precioKit.toString());
      setMesesGratisConKit(config.mesesGratisConKit.toString());
    }
  }, [config]);

  const actualizarMutation = useMutation({
    mutationFn: () =>
      configuracionServicio.actualizar({
        precioKit: Number(precioKit),
        mesesGratisConKit: Number(mesesGratisConKit),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuracion'] });
      toast.success('Configuración actualizada');
    },
    onError: () => {
      toast.error('Error al actualizar la configuración');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!precioKit || Number(precioKit) <= 0) {
      toast.error('El precio del kit debe ser mayor a 0');
      return;
    }

    if (mesesGratisConKit === '' || Number(mesesGratisConKit) < 0) {
      toast.error('Los meses gratis deben ser 0 o más');
      return;
    }

    actualizarMutation.mutate();
  };

  if (isLoading) {
    return (
      <div>
        <Header titulo="Configuración" />
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header titulo="Configuración" />

      <div className="p-4 sm:p-6">
        <div className="max-w-2xl space-y-6">
          {/* Kit de Instalacion */}
          <Card>
            <CardHeader
              title="Kit de Instalación"
              subtitle="Configura el precio del kit y los meses gratis incluidos"
            />
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Precio del Kit (MXN)"
                type="number"
                value={precioKit}
                onChange={(e) => setPrecioKit(e.target.value)}
                placeholder="2000"
                helperText="Precio que pagarán las veterinarias por el kit de instalación"
                icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              />
              <Input
                label="Meses gratis con kit"
                type="number"
                value={mesesGratisConKit}
                onChange={(e) => setMesesGratisConKit(e.target.value)}
                placeholder="1"
                helperText="Meses de licencia gratis al pagar el kit de instalación"
                icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
              />
              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  loading={actualizarMutation.isPending}
                  leftIcon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                >
                  Guardar
                </Button>
              </div>
            </form>
          </Card>

          {/* Info Stripe */}
          <Card>
            <CardHeader
              title="Stripe"
              subtitle="Información de la configuración de pagos"
            />
            <div className="space-y-3">
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="text-sm font-medium text-stone-700">Variables de entorno requeridas</p>
                <div className="mt-2 space-y-1 text-sm text-stone-500 font-mono">
                  <p>STRIPE_SECRET_KEY</p>
                  <p>STRIPE_WEBHOOK_SECRET</p>
                  <p>STRIPE_PRICE_KIT</p>
                  <p>STRIPE_PRICE_MENSUAL</p>
                  <p>STRIPE_PRICE_ANUAL</p>
                </div>
              </div>
              <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg">
                <p className="text-sm text-primary-700">
                  Los precios de Stripe (Price IDs) se configuran como variables de entorno en el backend. El precio del kit en esta página se usa como referencia; el cobro real se hace con el Price ID de Stripe.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Configuracion;
